import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AbsenceListResponse, RosterResponse, SubstitutePoolResponse, LeagueSubstituteContextResponse } from '@league/shared/contract';
import { effectivePace } from '@league/shared/schemas';
import { paceLabel } from '../../content/playing-preferences';
import { apiFetch } from '../../lib/api';
import { ErrorNote } from '../../components/ui';
import { comparePeople } from './people-display';
import { Facts, MobileList, MobileRow, pacePhrase, Sheet, SHEET_DONE, SHEET_LINK } from '../../components/MobileList';
import { PaceSelect } from './PaceSelect';
import { PLAYER_NAME, PLAYER_ROW, PlayerTable, Td, Th } from '../../components/PlayerTable';

/** Read-only composition of the existing authorized pool and this league's assignment readers. */
export function LeagueSubs({ leagueId, roster, search }: { leagueId: string; roster: RosterResponse['roster']; search: string }) {
  /** The substitute whose panel is open on a phone. */
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['league-subs', leagueId],
    queryFn: async () => {
      const { league, sessions } = await apiFetch<LeagueSubstituteContextResponse>(`/api/leagues/${leagueId}`);
      const [pool, assignments] = await Promise.all([
        apiFetch<SubstitutePoolResponse>(`/api/markets/${league.marketId}/substitutes`),
        Promise.all(sessions.map(async session => ({ session, ...(await apiFetch<AbsenceListResponse>(`/api/sessions/${session.id}/absences`)) }))),
      ]);
      return { marketId: league.marketId, pool: pool.substitutes, assignments };
    },
  });
  if (query.isPending) return <p className="text-sm text-ink-2">Loading subs…</p>;
  if (query.isError || !query.data) return <ErrorNote>Could not load substitutes. Try again.</ErrorNote>;
  const { marketId, pool, assignments } = query.data;
  const manage = `/director/players/subs?market=${encodeURIComponent(marketId)}`;
  const people = pool.filter(e => e.substitute.active && !roster.some(r => r.player.id === e.player.id && r.enrollment.status === 'enrolled'))
    .filter(e => `${e.player.firstName} ${e.player.lastName} ${e.player.email ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a,b) => comparePeople(a.player,b.player) || a.player.id.localeCompare(b.player.id));
  return <section className="flex flex-col gap-3">
    <Link to={manage} className="self-start text-sm underline underline-offset-4 focus-visible:outline focus-visible:outline-2">Add or manage substitutes</Link>
    {!people.length ? <p className="text-sm text-ink-2">No available substitutes{search ? ' match this search' : ''}.</p> : <PlayerTable head={<><Th>Player</Th><Th>Pace of play</Th><Th>Assigned</Th></>}>
      {people.map(entry => {
        const rosterEntry = roster.find(r => r.player.id === entry.player.id);
        const assigned = assignments.filter(a => a.absences.some(v => v.absence.subPlayerId === entry.player.id));
        return <tr key={entry.player.id} className={`${PLAYER_ROW} hover:bg-chalk`}>
          <Td kind="name">
            <Link to={`/director/players/${entry.player.id}`} className={PLAYER_NAME}>{entry.player.firstName} {entry.player.lastName}</Link>
          </Td>
          <Td className="whitespace-nowrap sm:pl-1">
            {rosterEntry ? <PaceSelect entry={rosterEntry} leagueId={leagueId} /> : <span className="px-2 text-[14px] text-ink">{paceLabel(effectivePace(entry.player.preferredPace, null))}</span>}
          </Td>
          <Td>
            {assigned.map(a => <Link key={a.session.id} to={`/director/sessions/${a.session.id}/seating`} className="mr-3 inline-block text-[14px] underline underline-offset-4 focus-visible:outline focus-visible:outline-2">Session {a.session.sessionNumber}</Link>)}
          </Td>
        </tr>;
      })}
    </PlayerTable>}
    {people.length ? <MobileList>
      {people.map(entry => {
        const assigned = assignments.filter(a => a.absences.some(v => v.absence.subPlayerId === entry.player.id));
        const rosterEntry = roster.find(r => r.player.id === entry.player.id);
        const pace = effectivePace(entry.player.preferredPace, rosterEntry?.enrollment.paceOverride ?? null);
        return <MobileRow key={entry.player.id} firstName={entry.player.firstName} lastName={entry.player.lastName} onOpen={() => setOpenId(entry.player.id)}
          lines={[pacePhrase(paceLabel(pace), pace === 'unknown'), assigned.length ? `Assigned ${assigned.map(a => `Session ${a.session.sessionNumber}`).join(', ')}` : null]} />;
      })}
    </MobileList> : null}
    {people.filter(entry => entry.player.id === openId).map(entry => {
      const rosterEntry = roster.find(r => r.player.id === entry.player.id);
      const assigned = assignments.filter(a => a.absences.some(v => v.absence.subPlayerId === entry.player.id));
      return <Sheet key={entry.player.id} open onClose={() => setOpenId(null)} title={`${entry.player.firstName} ${entry.player.lastName}`} subtitle={entry.player.email ?? 'No email'}
        footer={<><Link to={`/director/players/${entry.player.id}`} className={SHEET_LINK}>View player profile</Link><button type="button" className={SHEET_DONE} onClick={() => setOpenId(null)}>Done</button></>}>
        {rosterEntry
          ? <label className="flex flex-col gap-1.5"><span className="text-[13px] text-ink-3">Pace of play</span><PaceSelect entry={rosterEntry} leagueId={leagueId} field /></label>
          : <Facts items={[{ label: 'Pace of play', value: paceLabel(effectivePace(entry.player.preferredPace, null)) }]} />}
        {assigned.length ? <div><p className="mb-1.5 text-[13px] text-ink-3">Assigned</p>{assigned.map(a => <Link key={a.session.id} to={`/director/sessions/${a.session.id}/seating`} className={`mr-4 ${SHEET_LINK}`}>Session {a.session.sessionNumber}</Link>)}</div> : null}
      </Sheet>;
    })}
  </section>;
}
